import { Box, Checkbox, FormControl, InputLabel } from "@mui/material";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { Slider, Stack, TextField } from "@mui/material";
import { StyledCardAdd } from "./../TrackComponents/TrackMenuButton/styles";
import { SortFilterContainer, useStyles } from "../../pages/MyTracks/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useEffect, useState } from "react";
import { TrackObj } from "../../pages/MyTracks/types";
import { SelectField } from "../../pages/ExploreArtists/style";
import { MyTrackControlsProps, SORT_OPTIONS } from "./type";
import { styles } from "./styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { sortHelperMap } from "./sortHelper";
import { APP_COLOR, FONT_FAMILY } from "../../theme";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const MyTrackControls: React.FC<MyTrackControlsProps> = ({
  tracks,
  setSelectedTrack,
  collaboratorsOptionsList,
  allArtistList,
  selectedTrack,
  currentGenres,
  bpmCache,
  handleClearFilters,
}) => {
  const [genre, setGenre] = useState("");
  const [artist, setArtist] = useState("");
  const [sortValue, setSortValue] = useState<string>(
    SORT_OPTIONS.LAST_MODIFIED
  );
  const [isAscending, setIsAscending] = useState(false);
  const [collaboratorsList, setCollaboratorsList] = useState<string[]>([]);
  const [isAllFilterAndSortEmpty, SetIsAllFilterAndSortEmpty] = useState(true);
  const [bpmFilter, setBpmFilter] = useState([0, 200]);

  const classes = useStyles();

  const filterFunction = (
    track: TrackObj | undefined,
    bpmFilter: number[],
    genre: string,
    artist: string,
    collaborators: string[]
  ) => {
    if (!track) return false;
    const trackBpm = bpmCache.get(track.track!.getTrackVersionId());
    let bpmFilterCase = true;
    if (bpmFilter[0] >= 0 || bpmFilter[1] <= 200) {
      bpmFilterCase =
        trackBpm !== undefined &&
        trackBpm >= bpmFilter[0] &&
        trackBpm <= bpmFilter[1];
    }
    let genreCase = true;
    if (genre !== "") {
      genreCase = track!.track!.getSongMetadata()?.getGenre() === genre;
    }
    let artistCase = true;
    if (artist !== "") {
      artistCase = track!.track!.getArtist() === artist;
    }
    let collaboratorsCase = true;
    if (collaborators.length > 0) {
      collaboratorsCase = track!
        .track!.getCollaboratorsList()
        .some((item) => collaborators.includes(item.getUsername()));
    }
    return bpmFilterCase && genreCase && artistCase && collaboratorsCase;
  };

  const handleGenreChange = async (event: SelectChangeEvent) => {
    const g = event.target.value as string;
    setGenre(g);
    setSelectedTrack(
      tracks.filter((el: TrackObj) => {
        return filterFunction(el, bpmFilter, g, artist, collaboratorsList);
      })
    );
  };

  const handleBpmFilter = async (
    event: React.SyntheticEvent | Event,
    value: number | number[]
  ) => {
    if (Array.isArray(value)) {
      const bpmFilter = value as number[];
      setBpmFilter(bpmFilter);
      setSelectedTrack(
        tracks.filter((el: TrackObj) => {
          return filterFunction(
            el,
            bpmFilter,
            genre,
            artist,
            collaboratorsList
          );
        })
      );
    }
  };

  const handleArtistSelect = async (event: SelectChangeEvent) => {
    const a = event.target.value as string;
    setArtist(a);
    setSelectedTrack(
      tracks.filter((el: TrackObj) => {
        return filterFunction(el, bpmFilter, genre, a, collaboratorsList);
      })
    );
  };

  const handleChangeCollaboratorsList = async (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }
    setCollaboratorsList([...tempArr]);
    setSelectedTrack(
      tracks.filter((el: TrackObj) => {
        return filterFunction(el, bpmFilter, genre, artist, tempArr);
      })
    );
  };

  const sort = (value: string, asc: boolean) => {
    const data = sortHelperMap[value](selectedTrack ?? tracks);
    if (data.length > 0) SetIsAllFilterAndSortEmpty(false);
    if (!asc) {
      setSelectedTrack([...data].reverse());
    } else setSelectedTrack([...data]);
  };

  const handleClearSortAndFilter = () => {
    setIsAscending(false);
    setGenre("");
    setArtist("");
    setCollaboratorsList([]);
    setSortValue(SORT_OPTIONS.LAST_MODIFIED);
    // setSelectedTrack(tracks);
    handleClearFilters();
    setBpmFilter([0, 200]);
    SetIsAllFilterAndSortEmpty(true);
    sort(SORT_OPTIONS.LAST_MODIFIED, false);
  };

  const handleAscendingAndDescending = () => {
    sort(sortValue, !isAscending);
    setIsAscending((prevIsAscending) => !prevIsAscending);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    if (!event.target.value) return;
    setSortValue(event.target.value as string);
    if (
      (event.target.value as string) !== SORT_OPTIONS.LAST_MODIFIED &&
      !isAscending
    ) {
      SetIsAllFilterAndSortEmpty(false);
    }
    sort(event.target.value, isAscending);
  };

  useEffect(() => {
    if (
      sortValue === SORT_OPTIONS.LAST_MODIFIED &&
      !isAscending &&
      artist === "" &&
      genre === "" &&
      collaboratorsList.length === 0 &&
      JSON.stringify(bpmFilter) === JSON.stringify([0, 200])
    ) {
      SetIsAllFilterAndSortEmpty(true);
    } else {
      SetIsAllFilterAndSortEmpty(false);
    }
  }, [
    artist,
    collaboratorsList.length,
    sortValue,
    isAscending,
    genre,
    bpmFilter,
  ]);

  return (
    <>
      <SortFilterContainer id="step-2">
        <StyledCardAdd
          disabled={isAllFilterAndSortEmpty}
          onClick={handleClearSortAndFilter}
          sx={{
            marginRight: "2px",
            color: isAllFilterAndSortEmpty ? "grey" : "black",
            backgroundColor: isAllFilterAndSortEmpty
              ? "rgba(0,0,0,0)"
              : APP_COLOR,
            "&:hover": {
              backgroundColor: isAllFilterAndSortEmpty
                ? "rgba(0,0,0,0)"
                : APP_COLOR,
            },
          }}
        >
          <CloseIcon /> Clear
        </StyledCardAdd>
        {/* Genre  */}
        <FormControl sx={{ width: "200px" }}>
          <InputLabel id="select-label" sx={{ fontFamily: FONT_FAMILY }}>
            Genre
          </InputLabel>
          <Select
            sx={styles.selectField}
            labelId="select-label"
            value={genre}
            label="Genre"
            onChange={handleGenreChange}
          >
            {currentGenres.map((el, i) => {
              return (
                <MenuItem
                  key={i}
                  value={el}
                  sx={{ backgroundColor: "inherit" }}
                  classes={{ root: classes.selectStyle }}
                >
                  {el}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {/* Artist  */}
        <FormControl sx={{ width: "200px" }}>
          <InputLabel id="select-label" sx={{ fontFamily: FONT_FAMILY }}>
            Artist
          </InputLabel>
          <Select
            sx={styles.selectField}
            labelId="select-label"
            value={artist}
            label="Artist"
            onChange={handleArtistSelect}
          >
            {allArtistList
              .filter((el) => el !== "")
              .map((el, i) => {
                return (
                  <MenuItem
                    key={i}
                    value={el}
                    sx={{ backgroundColor: "inherit" }}
                    classes={{ root: classes.selectStyle }}
                  >
                    {el}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>

        {/* Collaborators  */}
        <Stack spacing={3}>
          <FormControl sx={{ borderColor: "white" }} id="step-4">
            <InputLabel id="select-label" sx={{ fontFamily: FONT_FAMILY }}>
              {collaboratorsList.length > 0 ? "" : "Collaborators"}
            </InputLabel>
            <SelectField
              multiple
              style={{ width: "250px" }}
              id="collaborator-tags"
              options={collaboratorsOptionsList}
              disableCloseOnSelect
              value={collaboratorsList}
              onChange={handleChangeCollaboratorsList}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                  <>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option}
                  </>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  sx={{ width: "250px" }}
                  {...params}
                  label={""}
                  InputLabelProps={{ shrink: false }}
                  classes={{ root: classes.customTextField }}
                />
              )}
            />
          </FormControl>
        </Stack>

        {/* Sort*/}
        <FormControl sx={{ width: "200px" }} id="step-5">
          <InputLabel id="select-label" sx={{ fontFamily: FONT_FAMILY }}>
            Sort By
          </InputLabel>
          <Select
            sx={styles.selectField}
            labelId="select-label"
            value={sortValue}
            label="Sort By"
            onChange={(e) => {
              handleSortChange(e);
            }}
          >
            {Object.values(SORT_OPTIONS).map((el, i) => {
              return (
                <MenuItem
                  key={i}
                  value={el}
                  sx={{ backgroundColor: "inherit" }}
                  classes={{ root: classes.selectStyle }}
                >
                  {el}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Box sx={styles.arrowContainer} onClick={handleAscendingAndDescending}>
          {isAscending ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        </Box>
        <Box sx={styles.sliderContainer}>
          BPM ({bpmFilter[0]} - {bpmFilter[1]})
          <Slider
            id="step-6"
            size="small"
            defaultValue={0}
            sx={{ color: APP_COLOR }}
            valueLabelDisplay="auto"
            value={bpmFilter}
            min={0}
            max={200}
            onChange={handleBpmFilter}
            onChangeCommitted={handleBpmFilter}
          />
        </Box>
      </SortFilterContainer>
    </>
  );
};
